import { Injectable } from '@angular/core';
import { User } from '@auth0/auth0-angular';
import { ReplaySubject } from 'rxjs';
import { AppConfig } from '../../app-config/app-config';

@Injectable({ providedIn: 'root' })
export class PendoService {
  private scriptLoaded = false;
  private appConfig!: AppConfig;
  private readonly _isPendoScriptLoaded = new ReplaySubject<boolean>(1);
  public readonly isPendoScriptLoaded =
    this._isPendoScriptLoaded.asObservable();

  constructor() {
    this._isPendoScriptLoaded.next(false);
  }

  loadScript(appConfig: AppConfig): Promise<void> {
    this.appConfig = appConfig;
    if (this.scriptLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://cdn.pendo.io/agent/static/${appConfig.pendoApiKey}/pendo.js`;
      script.async = true;
      script.onload = () => {
        this.setupPendoStub();
        this.scriptLoaded = true;
        this._isPendoScriptLoaded.next(true);
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load Pendo script'));
      document.head.appendChild(script);
    });
  }

  private setupPendoStub() {
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    let pendo = (window as any).pendo;
    if (!pendo) {
      pendo = {};
    }
    pendo._q = [];
    const methods = [
      'initialize',
      'identify',
      'updateOptions',
      'pageLoad',
      'track',
    ];

    for (const method of methods) {
      pendo[method] =
        pendo[method] ||
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        function (...args: any[]) {
          const queueMethod = method === 'initialize' ? 'unshift' : 'push';
          pendo._q[queueMethod]([method, ...args]);
        };
    }
  }

  initialize(user: User) {
    if (!this.scriptLoaded) {
      console.warn('Pendo script not loaded yet.');
      return;
    }

    if (!this.appConfig.usePendo) {
      return;
    }
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    let pendo = (window as any).pendo;
    if (!pendo) {
      pendo = {};
    }

    const visitor = {
      id: user.email,
      email: user.email,
      // eslint-disable-next-line camelcase
      full_name: user.name,
    };
    pendo.initialize({
      visitor,
      account: {
        id: user.email,
      },
    });
  }
}
